import { useState, useEffect, useCallback } from "react";
import { fetchAuthed, ApiError } from "@/utils/api";
import type { GameDetailResponse } from "@/types/api";

interface UseGameDetailReturn {
  game: GameDetailResponse | null;
  loading: boolean;
  error: string | null;
  errorStatus: number | null;
  refresh: () => Promise<void>;
}

export function useGameDetail(gameId: string): UseGameDetailReturn {
  const [game, setGame] = useState<GameDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await fetchAuthed<GameDetailResponse>(
        gameId,
        `/games/${gameId}`,
      );
      setGame(data);
      setError(null);
      setErrorStatus(null);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setErrorStatus(err.status);
      } else {
        setError("Failed to load game");
        setErrorStatus(null);
      }
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh]);

  return { game, loading, error, errorStatus, refresh };
}
